import React, { useState, useEffect } from "react";
import {
  Radio,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { platformState, userInfoState } from "../global-state";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import LoadingSpinner from "../common/loading";
import MSwitch from "../common/switch";
import { useToast } from "../common/toast";
import Button from "@mui/material/Button";
import useApiCall from "../common/api-call";
import {
  getHubList,
  electHub,
  rejectHub,
  restartHub,
  saveHub,
  getHubStatus,
} from "./hub-service";
import { registerDesktopDevice } from "../setting/desktop-permission-service";

const HubSetting = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const platform = useRecoilValue(platformState);
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const isApp = platform?.source === "app";
  const isDesktop = platform?.source === "desktop";
  const { successToast, warningToast, errorToast } = useToast();
  const [candidates, setCandidates] = useState([]);
  const [activeHubStatus, setActiveHubStatus] = useState(null);
  const { apiCall } = useApiCall();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogTitle, setConfirmDialogTitle] = useState("");
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);

  // Get device ID for desktop platform
  const getDesktopDeviceId = () => {
    if (isDesktop && platform?.info?.deviceId) {
      return platform.info.deviceId;
    }
    return null;
  };

  // Function to fetch hub statuses
  const fetchHubStatuses = async () => {
    if (!userInfo?.activeScope?.id) {
      return;
    }

    try {
      const response = await getHubStatus(apiCall, userInfo.activeScope.id);
      if (response && !response.error) {
        setActiveHubStatus(response);
      }
    } catch (error) {
      console.error("Error fetching hub status:", error);
    }
  };

  // Set up interval to fetch hub statuses every minute, but only when page is visible
  useEffect(() => {
    // Reference to track if page is visible
    const isPageVisible = { current: true };
    let statusInterval = null;

    // Function to handle visibility changes
    const handleVisibilityChange = () => {
      isPageVisible.current = document.visibilityState === "visible";

      if (isPageVisible.current) {
        // If page becomes visible, fetch immediately and restart interval
        fetchHubStatuses();
        statusInterval = setInterval(() => {
          if (isPageVisible.current) {
            fetchHubStatuses();
          }
        }, 60000); // 1 minute
      } else {
        // If page becomes hidden, clear the interval
        if (statusInterval) {
          clearInterval(statusInterval);
          statusInterval = null;
        }
      }
    };

    // Initial fetch
    fetchHubStatuses();

    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Clean up interval and event listener on component unmount
    return () => {
      if (statusInterval) {
        clearInterval(statusInterval);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [userInfo?.activeScope?.id]);

  // Modify the useEffect to also check for phone connection status
  useEffect(() => {
    if (isApp && window.Android) {
      window.onHubRestarted = (result) => {
        const resp = JSON.parse(result);
        if (!resp.error) {
          successToast("Success");
        } else {
          errorToast("Error!");
        }
        setLoading(false);
      };
      window.onHubList = (result) => {
        const hubList = JSON.parse(result);
        if (!hubList.error) {
          setCandidates(hubList.hubs || []);
        } else {
          setCandidates([]);
        }
        setLoading(false);
      };
      window.onHubElected = (result) => {
        const resp = JSON.parse(result);
        if (!resp.error) {
          successToast("Set hub success");
          handleHubListFetch();
        } else {
          errorToast("Error!");
        }
      };
      window.onHubRejected = (result) => {
        const resp = JSON.parse(result);
        if (!resp.error) {
          successToast("Remove hub success");
          handleHubListFetch();
        } else {
          errorToast("Error!");
        }
      };
      handleHubListFetch();
    } else {
      handleHubListFetch();
    }
  }, [isApp, isDesktop]);

  const canBeToggled = (hub) => {
    // Only allow toggling if it's the user's own device
    if (hub.me === true) {
      return true;
    }

    // For other devices, only allow rejecting (turning off)
    if (hub.active === true) {
      return true;
    }

    if (hub.hubType === "HUB") {
      return true;
    }

    return false;
  };

  const handleHubElect = async (elect, hub) => {
    // Only allow electing if it's current device
    if (elect) {
      if (hub.hubType !== "HUB" && !hub.me) {
        errorToast("Not allowed");
        return;
      }
    }

    if (elect) {
      setConfirmDialogTitle("Switch Hub");
      setConfirmDialogMessage(
        `Do you want to use ${hub.name} as local hub? Current hub (if any) will stop running. During switch, some functionalities may get interrupted.`
      );
    } else {
      setConfirmDialogTitle("Stop Hub");
      setConfirmDialogMessage(
        "Do you want to stop the local hub. Some functionalities may get error."
      );
    }
    setConfirmDialogAction(() => () => performHubElect(elect, hub));
    setConfirmDialogOpen(true);
  };

  const performHubElect = async (elect, hub = null) => {
    console.log("performHubElect", elect, hub);
    if (!hub.me) {
      setLoading(true);
      const response = elect
        ? await electHub(
            apiCall,
            userInfo.activeScope.id,
            hub.deviceId,
            "DESKTOP"
          )
        : await rejectHub(
            apiCall,
            userInfo.activeScope.id,
            hub.deviceId,
            "DESKTOP"
          );
      if (response && !response.error) {
        successToast(elect ? "Elect hub success" : "Remove hub success");
      } else {
        errorToast("Error!");
      }
    } else if (isApp && window.Android) {
      if (elect) {
        window.Android.electHub();
      } else {
        window.Android.rejectHub();
      }
    } else if (isDesktop) {
      const deviceId = getDesktopDeviceId();
      try {
        setLoading(true);
        const response = elect
          ? await electHub(
              apiCall,
              userInfo.activeScope.id,
              deviceId,
              "DESKTOP"
            )
          : await rejectHub(
              apiCall,
              userInfo.activeScope.id,
              deviceId,
              "DESKTOP"
            );

        if (response && !response.error) {
          // Store hubId and start/stop hub using the utility functions
          const hubInfo = await window.electron.ipcRenderer.invoke(
            "hub:get-info"
          );
          if (elect && response.hubId) {
            hubInfo[userInfo.activeScope.id] = { hubId: response.hubId };
            await window.electron.ipcRenderer.invoke("hub:store-info", hubInfo);
            await window.electron.ipcRenderer.invoke(
              "hub:start",
              userInfo.activeScope.id
            );
          } else if (!elect) {
            delete hubInfo[userInfo.activeScope.id];
            await window.electron.ipcRenderer.invoke("hub:store-info", hubInfo);
            await window.electron.ipcRenderer.invoke(
              "hub:stop",
              userInfo.activeScope.id
            );
          }

          successToast(elect ? "Set hub success" : "Remove hub success");
          // Refresh hub list
        } else {
          errorToast("Error");
        }
      } catch (error) {
        console.error("Error in hub election:", error);
        errorToast("Error");
      }
    }
    handleHubListFetch();
  };

  const handleHubRestart = async () => {
    if (isApp && window.Android) {
      window.Android.restartHub();
    } else if (isDesktop) {
      if (!userInfo?.activeScope?.id) {
        errorToast("No active scope found");
        return;
      }

      const deviceId = getDesktopDeviceId();
      if (!deviceId) {
        errorToast("Device ID not available");
        return;
      }

      try {
        setLoading(true);
        const response = await restartHub(
          apiCall,
          userInfo.activeScope.id,
          deviceId
        );

        if (response && !response.error) {
          successToast("Hub restarted successfully");
        } else {
          errorToast(response?.error || "Failed to restart hub");
        }
      } catch (error) {
        console.error("Error restarting hub:", error);
        errorToast("Failed to restart hub");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleHubListFetch = async () => {
    setLoading(true);
    if (isApp && window.Android) {
      window.Android.getHubList();
    } else {
      if (!userInfo?.activeScope?.id) {
        setLoading(false);
        return;
      }

      try {
        const deviceId = isDesktop ? getDesktopDeviceId() : "";
        const response = await getHubList(
          apiCall,
          userInfo.activeScope.id,
          deviceId,
          isDesktop ? "DESKTOP" : "UNKNOWN"
        );

        if (response && !response.error) {
          setCandidates(response.hubs || []);
        } else {
          setCandidates([]);
          if (response?.error) {
            errorToast(`Error getting hubs`);
          }
        }
      } catch (error) {
        console.error("Error getting hubs:", error);
        errorToast("Error getting hubs");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRegisterAsHub = async () => {
    if (!userInfo?.activeScope?.id) {
      errorToast("No active scope found");
      return;
    }

    const deviceId = getDesktopDeviceId();
    if (!deviceId) {
      errorToast("Device ID not available");
      return;
    }

    try {
      setLoading(true);

      // First register the device
      const deviceData = {
        deviceId: deviceId,
        deviceName: platform?.info?.deviceName,
        scopeId: userInfo.activeScope.id,
      };

      if (isDesktop) {
        const registerResponse = await registerDesktopDevice(
          apiCall,
          deviceData
        );
        if (!registerResponse) {
          errorToast("Error!");
          setLoading(false);
          return;
        }
      } else {
        // TODO: Register phone device
        return;
      }

      // Then save as hub using the hub-service function
      const saveHubResponse = await saveHub(
        apiCall,
        userInfo.activeScope.id,
        deviceId,
        isDesktop ? "DESKTOP" : "PHONE"
      );

      if (saveHubResponse && !saveHubResponse.error) {
        successToast("Device registered as hub successfully");
        // Refresh hub list
        handleHubListFetch();
      } else {
        errorToast("Error!");
      }
    } catch (error) {
      errorToast("Error!");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmDialogConfirm = () => {
    if (confirmDialogAction) {
      confirmDialogAction();
    }
    setConfirmDialogOpen(false);
  };

  const hasMeHub = candidates.some((hub) => hub.me);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        p: { xs: 2, md: 3 },
      }}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Register Current Device as Hub Button - Only for app/desktop */}
          {(isDesktop || isApp) && !hasMeHub && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRegisterAsHub}
                fullWidth
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  py: 1,
                }}
              >
                Register current device as Hub
              </Button>
            </Box>
          )}

          <Grid container spacing={2}>
            {candidates.length > 0 ? (
              candidates.map((hub, index) => (
                <Grid item xs={12} key={index}>
                  <Card
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: "12px",
                      boxShadow: "none",
                      opacity: canBeToggled(hub) ? 1 : 0.5,
                    }}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "medium" }}
                          >
                            {hub.name}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" color="textSecondary">
                              {hub.hubType}
                            </Typography>
                            {hub.me && (
                              <Chip
                                label="This Device"
                                size="small"
                                color="secondary"
                                sx={{
                                  height: "20px",
                                  "& .MuiChip-label": {
                                    px: 1,
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                            )}
                          </Box>
                          {/* Add health status chip */}
                          {hub.active && activeHubStatus && (
                            <Box
                              sx={{
                                mt: 1,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Chip
                                label={
                                  activeHubStatus.healthy
                                    ? "Healthy"
                                    : "Not Healthy"
                                }
                                size="small"
                                color={
                                  activeHubStatus.healthy ? "success" : "error"
                                }
                                sx={{
                                  height: "20px",
                                  "& .MuiChip-label": {
                                    px: 1,
                                    fontSize: "0.75rem",
                                  },
                                }}
                              />
                              <Tooltip title="Refresh hub status">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetchHubStatuses();
                                  }}
                                  sx={{ ml: 0.5, p: 0.5 }}
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          {/* Add restart button for active hub */}
                          {hub.active && (
                            <Box sx={{ mt: 1 }}>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleHubRestart();
                                }}
                                disabled={
                                  !activeHubStatus || !activeHubStatus?.healthy
                                }
                                size="small"
                                sx={{
                                  borderRadius: "4px",
                                  textTransform: "none",
                                  py: 0.5,
                                  px: 1,
                                  fontSize: "0.75rem",
                                  boxShadow: "none",
                                }}
                              >
                                Restart Hub
                              </Button>
                            </Box>
                          )}
                        </Box>
                        <FormControlLabel
                          control={
                            <MSwitch
                              size="small"
                              checked={hub.active}
                              onChange={(event) =>
                                handleHubElect(event.target.checked, hub)
                              }
                              disabled={!canBeToggled(hub)}
                            />
                          }
                          label=""
                          onClick={(event) => event.stopPropagation()}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Card
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "12px",
                    boxShadow: "none",
                  }}
                >
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        py: 2,
                      }}
                    >
                      <Typography variant="body1" color="textSecondary">
                        No available hubs found
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {/* Info Message Box */}
      <Card
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: "12px",
          boxShadow: "none",
          mt: 2,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InfoOutlinedIcon
              sx={{
                color: theme.palette.primary.main,
                mr: 2,
              }}
            />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                What is a hub?
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Hub is a master device running mHome software in your local
                network. It&apos;s a communication bridge between your local
                smart home devices and the mHome cloud. And it provides
                on-device engine to process data and run automations locally for
                stronger privacy.
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Currently supported hub devices:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                • Android phone / tablet with mHome App installed
                <br />• Windows / MacOS / Linux desktop with mHome desktop
                client installed
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Choose the right hub for your home:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Only one device can be selected as hub. If you have multiple
                available hub devices, you can choose the right hub for your
                home.
                <br />
                • mHome smart Hub. This is the best choice. But unfortunately,
                we don&apos;t have it yet.
                <br />
                • Phone / tablet is recommended, as it&apos;s always online 24/7
                when continuously charged. Remember to grant the necessary
                permissions to keep the App running; and always have the device
                in your local network.
                <br />• Otherwise, use Desktop as hub. It auto connects when the
                desktop client is running.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {confirmDialogTitle}
        </DialogTitle>
        <DialogContent>
          <Typography id="confirm-dialog-description">
            {confirmDialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDialogConfirm}
            color="primary"
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HubSetting;
